// ── Token cost estimation (client-side) ─────────────────────────────────────
// Turns a token-volume breakdown (sumTokenBreakdown shape from tokenVolume.js)
// into a USD cost estimate, using pi-ai's bundled per-model pricing
// (modelCatalog.getModelCost). Entirely in-browser — no backend, no network.
//
// The accounting deliberately mirrors tokenVolume.js so cost and volume never
// disagree about what a token is:
//
//   • cacheRead / cacheCreation are additive only for cache-disjoint providers
//     (Anthropic), where pi-ai's 4-way `cost` maps 1:1 onto the four volume
//     bands. For inclusive-input providers, inclusiveCacheRead is a subset of
//     input: subtract it from fresh input and reprice it at cacheRead. When the
//     catalog lacks that rate, fall back to the input rate.
//
//   • reasoning is NOT priced. It already lives inside output_tokens (Claude
//     folds thinking into output) or is billed at the output rate inside
//     output (OpenAI o-series), so output × outputRate counts it exactly once.
//     Charging the reasoning band again would invent cost that was never
//     billed — the same "subset of output, never additive" rule tokenVolume.js
//     enforces for volume.
//
// Caveats the caller owns:
//   • Prices are frozen at the pinned pi-ai version — treat the result as an
//     ESTIMATE at current rates; historical data is NOT re-priced to the rate
//     in effect when it was billed. Bumping prices means bumping pi-ai (same
//     as the model catalog).
//   • Unmatched (provider, model) → null. Render volume only; never a $0 that
//     reads as "free."
//
// COVERAGE IS PART OF THE ANSWER. Because an unmatched model contributes zero
// dollars while still contributing volume, a rollup total is only as meaningful
// as the share of tokens that actually priced — and the catalog goes stale by
// default, one model launch at a time. `summarizeTokenCost` therefore returns
// that share alongside the total, and sets `suppressTotal` once it drops below
// COST_COVERAGE_FLOOR. Surfaces MUST honour `suppressTotal`: an estimate that
// silently omits the model doing the work is worse than no estimate, because it
// reads as "spend is negligible" rather than "spend is unknown."

import { getModelCost } from "./modelCatalog.js";

const PER_MILLION = 1_000_000;

// ── Master kill switch ───────────────────────────────────────────
// Cost estimates ship ON. EVERY cost surface — the chart legend total +
// per-model hover, the trace-list summary bar, the per-trace chip — gets its
// number from one of the two entry points below, and all three already
// render nothing when the cost is absent. So this is the single lever to
// suppress estimates everywhere if the numbers ever look wrong (a bad
// catalog price, a provider/model mismatch):
//   • Kill in the build: flip COST_ESTIMATES_ON to false (one line).
//   • Kill a live session WITHOUT a redeploy, from any browser console
//     (read live, no reload needed):
//        localStorage.setItem("dashboard:tokenCostOff", "1")
// This is a kill switch, not a feature gate — default ON, opt-OUT only.
const COST_ESTIMATES_ON = true;

export function tokenCostEnabled() {
    if (!COST_ESTIMATES_ON) return false;
    try {
        if (typeof localStorage !== "undefined" && localStorage.getItem("dashboard:tokenCostOff") === "1") {
            return false;
        }
    } catch {
        // localStorage blocked (private mode / sandbox) — stay enabled.
    }
    return true;
}

// Minimum share of PROCESSED tokens that must resolve to a catalogued rate
// before a rollup total is worth showing as a total. Below half, the figure
// stops being a usable lower bound: the un-priced remainder can exceed the
// priced part by any multiple, so the number carries no information about
// magnitude — only a floor of zero, which every total already has.
export const COST_COVERAGE_FLOOR = 0.5;

// The shape summarizeTokenCost returns when there's nothing to price (kill
// switch off, or no catalogued rows) — every surface keys off `priced`.
const EMPTY_COST_SUMMARY = Object.freeze({
    total: null,
    byModel: {},
    pricedWork: 0,
    unpricedWork: 0,
    pricedThroughput: 0,
    unpricedThroughput: 0,
    pricedShare: null,
    suppressTotal: false,
    hasUnpriced: false,
    priced: false,
});

// Integer percent of processed tokens that priced. FLOORED, so a sliver of
// un-priced traffic can never round up to a reassuring "100%" — 100 is
// reserved for a rollup where nothing went un-priced at all. Null when there
// was nothing to price (no data), which is not the same as 0% (data, no rates).
export function pricedPercent(summary) {
    const share = summary?.pricedShare;
    if (share == null) return null;
    if (share >= 1) return 100;
    return Math.max(0, Math.min(99, Math.floor(share * 100)));
}

// Pure: per-million-token USD `rates` ({ input, output, cacheRead, cacheWrite })
// applied to a token `breakdown`. Rates in, dollars out — no catalog lookup, so
// the arithmetic is unit-testable in isolation. Returns a per-band + total USD
// object, or null when `rates` is missing.
export function applyTokenRates(breakdown, rates) {
    if (!rates) return null;
    const b = breakdown || {};
    const num = (v) => Number(v) || 0;
    const inputTokens = num(b.input);
    const inclusiveCacheRead = Math.min(num(b.inclusiveCacheRead), inputTokens);
    const freshInput = inputTokens - inclusiveCacheRead;
    const inclusiveCacheRate = rates.cacheRead ?? rates.input;
    const input = freshInput * num(rates.input) + inclusiveCacheRead * num(inclusiveCacheRate);
    const output = num(b.output) * num(rates.output);
    const cacheRead = num(b.cacheRead) * num(rates.cacheRead);
    // tokenVolume.js calls the write band `cacheCreation`; pi-ai calls the rate
    // `cacheWrite`. Same thing — bridge the names here.
    const cacheWrite = num(b.cacheCreation) * num(rates.cacheWrite);
    return {
        input: input / PER_MILLION,
        output: output / PER_MILLION,
        cacheRead: cacheRead / PER_MILLION,
        cacheWrite: cacheWrite / PER_MILLION,
        total: (input + output + cacheRead + cacheWrite) / PER_MILLION,
    };
}

// Estimate USD cost for a token `breakdown` on a given (provider, model).
// Returns null when the model isn't in pi-ai's catalog (caller shows volume
// only). `breakdown` is the sumTokenBreakdown shape from tokenVolume.js.
export function estimateTokenCost(breakdown, { provider, model } = {}) {
    if (!tokenCostEnabled()) return null;
    return applyTokenRates(breakdown, getModelCost(provider, model));
}

// Sum per-(provider, model) token rows into USD BANDS — the input the Lanes
// density strip's calibrated attribution needs (traceTurnDensity.js splits
// spend into re-read / entry / output pools, so it needs dollars per band,
// not one total). Rows carry camelCase token fields (the adaptTrace shape:
// inputTokens / outputTokens / cacheReadTokens / cacheCreationTokens) plus
// `provider` and `model`. Pricing happens at row grain for the same reason
// summarizeTokenCost prices per group: a blended aggregate can't be priced.
//
// All-or-nothing honesty: `priced` is true only when EVERY row with work
// tokens resolved to a catalogued rate. A partial band total would read as
// "the whole thing cost $X" on a surface whose entire point is the total —
// callers should fall back to a smaller-but-fully-measured level (or mass
// only) instead. Returns { input, output, cacheRead, cacheWrite, total,
// priced, hasUnpriced }, dollars throughout; kill switch → unpriced.
export function sumCostBands(rows) {
    const bands = { input: 0, output: 0, cacheRead: 0, cacheWrite: 0, total: 0, priced: false, hasUnpriced: false };
    let pricedAny = false;
    for (const r of rows || []) {
        const c = estimateTokenCost(
            {
                input: r.inputTokens,
                output: r.outputTokens,
                cacheRead: r.cacheReadTokens,
                cacheCreation: r.cacheCreationTokens,
                inclusiveCacheRead: r.inclusiveCacheReadTokens,
            },
            { provider: r.provider, model: r.model },
        );
        if (c == null) {
            // Bytes-only rows (every token column null) are coverage floor, not
            // unpriced work — only rows with something to price count against
            // the all-or-nothing flag.
            const work = (Number(r.inputTokens) || 0) + (Number(r.outputTokens) || 0)
                + (Number(r.cacheReadTokens) || 0) + (Number(r.cacheCreationTokens) || 0);
            if (work > 0) bands.hasUnpriced = true;
            continue;
        }
        pricedAny = true;
        bands.input += c.input;
        bands.output += c.output;
        bands.cacheRead += c.cacheRead;
        bands.cacheWrite += c.cacheWrite;
        bands.total += c.total;
    }
    bands.priced = pricedAny && !bands.hasUnpriced;
    return bands;
}

// Roll a set of per-(provider, model) token rows up into one cost estimate.
// Each row carries the raw token columns (input_tokens / output_tokens /
// cache_read_tokens / cache_creation_tokens) plus `provider` and `model`. We
// MUST price at this grain and sum the dollars — a blended aggregate can't be
// priced (different models, different rates). Returns:
//   total        — summed USD across every priced group
//   byModel       — { [model]: usd } summed across providers, for the chart's
//                   per-model legend hover (layers are keyed by model alone)
//   pricedWork    — input+output tokens that resolved to a catalogued rate
//   unpricedWork  — input+output tokens with no catalog match (the honesty
//                   signal: a partial total under-counts by this much)
//   pricedThroughput / unpricedThroughput
//                 — the same split measured in PROCESSED tokens
//                   (input+output+cacheRead+cacheCreation). Coverage is
//                   weighted by throughput, not work, because that is what the
//                   dollars track: on an agentic Anthropic workload cache reads
//                   run 100-400× the uncached input, so a rollup can be ~70%
//                   priced by work while being ~50% priced by cost. Work-weighted
//                   coverage systematically overstates how complete the total is.
//   pricedShare   — pricedThroughput / all throughput, or null when there was
//                   nothing to price at all
//   suppressTotal — pricedShare fell below COST_COVERAGE_FLOOR: `total` is too
//                   incomplete to render as a total. Surfaces show the coverage
//                   gap instead of the number (see the header note).
//   hasUnpriced   — true when un-priced groups carried any tokens. Keyed on
//                   volume, not group count, so a zero-token group (a
//                   placeholder model tag, an all-NULL row) can't raise a
//                   "partial estimate" warning about nothing.
//   priced        — true when at least one group priced (else show nothing)
// `total` is null when nothing priced, so callers can distinguish "no data"
// from a real $0 (all-cached / free-tier).
export function summarizeTokenCost(rows) {
    if (!tokenCostEnabled()) return EMPTY_COST_SUMMARY;
    let total = 0;
    let pricedWork = 0;
    let unpricedWork = 0;
    let pricedThroughput = 0;
    let unpricedThroughput = 0;
    const byModel = {};
    for (const r of rows || []) {
        const breakdown = {
            input: Number(r.input_tokens) || 0,
            output: Number(r.output_tokens) || 0,
            cacheRead: Number(r.cache_read_tokens) || 0,
            cacheCreation: Number(r.cache_creation_tokens) || 0,
            inclusiveCacheRead: Number(r.inclusive_cache_read_tokens) || 0,
        };
        const work = breakdown.input + breakdown.output;
        // Mirrors tokenVolume.js THROUGHPUT. The cache bands are summed
        // provider-predicated upstream, so an inclusive-input provider
        // contributes input+output and never double-counts its cached portion.
        const throughput = work + breakdown.cacheRead + breakdown.cacheCreation;
        const c = estimateTokenCost(breakdown, { provider: r.provider, model: r.model });
        if (c == null) {
            unpricedWork += work;
            unpricedThroughput += throughput;
            continue;
        }
        pricedWork += work;
        pricedThroughput += throughput;
        total += c.total;
        const m = String(r.model ?? "");
        byModel[m] = (byModel[m] || 0) + c.total;
    }
    const priced = pricedWork > 0 || Object.keys(byModel).length > 0;
    // An all-zero rollup (rows present, every token column empty) has no
    // throughput to weigh, so there is no coverage claim to make — leave the
    // share null and let `priced` alone decide, rather than reporting 0% and
    // suppressing a legitimate $0.
    const allThroughput = pricedThroughput + unpricedThroughput;
    const pricedShare = allThroughput > 0 ? pricedThroughput / allThroughput : null;
    return {
        total: priced ? total : null,
        byModel,
        pricedWork,
        unpricedWork,
        pricedThroughput,
        unpricedThroughput,
        pricedShare,
        suppressTotal: pricedShare != null && pricedShare < COST_COVERAGE_FLOOR,
        hasUnpriced: unpricedThroughput > 0,
        priced,
    };
}
